// utils/chatHelpers.js
// --------------------
// Pure utility functions shared across the chat widget.
// These have no React state — they are plain JavaScript functions.

import { v4 as uuidv4 } from 'uuid';
import { FaUserCircle } from 'react-icons/fa';
import { TbMessageChatbot } from 'react-icons/tb';

/**
 * getUserId
 * ---------
 * Returns the ID that identifies the current user to the backend.
 *
 * Priority:
 *   1. If a `visitorId` was passed in the URL params, use that directly.
 *   2. Otherwise, look for a saved anonymous ID in localStorage,
 *      scoped per botId so each embedded chatbot gets its own user identity
 *      (prevents Larken Agent and Residential Agent from sharing sessions).
 *   3. If none exists yet, generate a new one and save it.
 *
 * @param {string|null} visitorId - The visitor ID from URL search params (or null).
 * @param {string}      botId     - The agent instance ID (e.g. "larken-agent-vie8").
 * @returns {string} The user ID to send to the backend.
 */
export const getUserId = (visitorId, botId = '') => {
  if (visitorId) {
    return visitorId;
  }
  // Scope the localStorage key per botId so different embedded bots
  // get independent anonymous user identities → fully isolated session lists.
  const storageKey = botId ? `bot_userId_${botId}` : 'bot_userId';
  let userId = localStorage.getItem(storageKey);
  if (userId == null || userId === '') {
    userId = `anonymous_${uuidv4().substring(0, 8)}`;
    localStorage.setItem(storageKey, userId);
  }
  return userId;
};

/**
 * mapBackendMessagesToReachat
 * ---------------------------
 * Converts the message format returned by the backend into the shape
 * that the Reachat chat component expects.
 *
 * Backend shape:  { id, text, sender, timestamp, ... }
 * Reachat shape:  { id, content, sender, timestamp, avatarIconComponent, ... }
 *
 * @param {object|object[]} backendMessages - One message or an array of messages.
 * @returns {object[]} Messages formatted for Reachat.
 */
export const mapBackendMessagesToReachat = (backendMessages) => {
  if (!backendMessages) return [];
  const messagesArray = Array.isArray(backendMessages) ? backendMessages : [backendMessages];
  return messagesArray.map(msg => ({
    id: msg.id,
    content: msg.text || msg.content,
    sender: msg.sender || msg.type,
    timestamp: msg.timestamp,
    reasoning:   msg.reasoning   || null,   // agent step-by-step trace (DEV_MODE only)
    suggestions: msg.suggestions || null,   // follow-up action chips below the bubble
    howFoundSummary: msg.howFoundSummary || null,   // plain-English "how I found this" recap, shown to all users
    fullTrace: msg.fullTrace || null,               // full technical step trace behind the recap, for "View full details"
    flowSteps: msg.flowSteps || null,
    receivedAt: msg.receivedAt || null,             // when the answer reached this browser (live answers only)               // the flow's steps that ran, for the Answer Trail's flow view
    isNew: msg.isNew || false,              // triggers typewriter animation on first render
    // Show a person icon for the user, a bot icon for everything else
    avatarIconComponent: (msg.sender === 'user' || msg.type === 'user') ? FaUserCircle : TbMessageChatbot,
    avatarColor: (msg.sender === 'user' || msg.type === 'user') ? '#10B981' : '#10B981',
    avatarBg:    (msg.sender === 'user' || msg.type === 'user') ? '#D1FAE5' : '#D1FAE5',
  }));
};
